import React, { useState, useEffect } from 'react';
import Radio from '../common/Radio';
import Input from '../common/Input';
import Button from '../common/Button';

const DeliveryForm = ({ data, products, onChange }) => {
  const [deliveryType, setDeliveryType] = useState(data.type || 'retirada');
  const [address, setAddress] = useState(data.address || '');
  const [city, setCity] = useState(data.city || '');
  const [state, setState] = useState(data.state || '');
  const [zipCode, setZipCode] = useState(data.zipCode || '');
  const [deliveryDates, setDeliveryDates] = useState(data.dates || [
    { date: '', items: [...products] }
  ]);

  // Atualizar estado quando os produtos mudarem
  useEffect(() => {
    if (products.length > 0 && (!deliveryDates || deliveryDates.length === 0)) {
      setDeliveryDates([{ date: '', items: [...products] }]);
    } else if (products.length > 0) {
      // Atualiza o primeiro grupo de entrega com os produtos atuais
      const updatedDates = [...deliveryDates];
      updatedDates[0] = { ...updatedDates[0], items: [...products] };
      setDeliveryDates(updatedDates);
    }
  }, [products, deliveryDates]);

  // Atualizar os dados do formulário
  useEffect(() => {
    onChange({
      type: deliveryType,
      address,
      city,
      state,
      zipCode,
      dates: deliveryDates
    });
  }, [deliveryType, address, city, state, zipCode, deliveryDates, onChange]);

  const handleDeliveryTypeChange = (type) => {
    setDeliveryType(type);
  };

  const handleDateChange = (index, newDate) => {
    const updatedDates = [...deliveryDates];
    updatedDates[index] = { ...updatedDates[index], date: newDate };
    setDeliveryDates(updatedDates);
  };

  const addDeliveryDate = () => {
    setDeliveryDates([...deliveryDates, { date: '', items: [] }]);
  };

  const removeDeliveryDate = (index) => {
    if (deliveryDates.length <= 1) return;
    
    const updatedDates = [...deliveryDates];
    updatedDates.splice(index, 1);
    setDeliveryDates(updatedDates);
  };

  const moveProductToDate = (productIndex, fromDateIndex, toDateIndex) => {
    if (fromDateIndex === toDateIndex) return;
    
    const updatedDates = [...deliveryDates];
    const product = { ...updatedDates[fromDateIndex].items[productIndex] };
    
    // Remover do grupo atual
    updatedDates[fromDateIndex].items.splice(productIndex, 1);
    
    // Adicionar ao novo grupo
    if (!updatedDates[toDateIndex].items) {
      updatedDates[toDateIndex].items = [];
    }
    updatedDates[toDateIndex].items.push(product);
    
    setDeliveryDates(updatedDates);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-800">Entrega</h2>
      
      <div className="space-y-6">
        <div className="flex flex-col space-y-2">
          <span className="text-sm font-medium text-gray-700">Tipo de Entrega</span>
          <div className="flex items-center gap-6">
            <Radio
              id="retirada"
              name="deliveryType"
              value="retirada"
              checked={deliveryType === 'retirada'}
              onChange={() => handleDeliveryTypeChange('retirada')}
              label="Retirada pelo cliente"
            />
            
            <Radio
              id="entrega"
              name="deliveryType"
              value="entrega"
              checked={deliveryType === 'entrega'}
              onChange={() => handleDeliveryTypeChange('entrega')}
              label="Entrega no endereço"
            />
          </div>
        </div>
        
        {deliveryType === 'entrega' && (
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            <h3 className="font-medium text-gray-700">Endereço de Entrega</h3>
            
            <Input
              label="Endereço Completo"
              id="address"
              name="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Rua, número, complemento"
              required
            />
            
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Cidade"
                id="city"
                name="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
              />
              
              <Input
                label="Estado"
                id="state"
                name="state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                maxLength={2}
                required
              />
            </div>
            
            <Input
              label="CEP"
              id="zipCode"
              name="zipCode"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              required
            />
          </div>
        )}
        
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-medium text-gray-700">
              {deliveryType === 'entrega' ? 'Datas de Entrega' : 'Datas de Retirada'}
            </h3>
            
            {deliveryDates.length < 3 && (
              <Button 
                type="button" 
                onClick={addDeliveryDate} 
                variant="outline"
                size="sm"
              >
                Adicionar Data
              </Button>
            )}
          </div>
          
          {deliveryDates.map((dateGroup, dateIndex) => (
            <div key={dateIndex} className="border border-gray-200 rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <Input
                  label={`Data de ${deliveryType === 'entrega' ? 'Entrega' : 'Retirada'} ${dateIndex + 1}`}
                  id={`delivery-date-${dateIndex}`}
                  name={`delivery-date-${dateIndex}`}
                  type="date"
                  value={dateGroup.date}
                  onChange={(e) => handleDateChange(dateIndex, e.target.value)}
                  className="flex-1"
                />
                
                {deliveryDates.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDeliveryDate(dateIndex)}
                    className="text-red-600 hover:text-red-900 ml-4 mt-6"
                  >
                    Remover
                  </button>
                )}
              </div>
              
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Produtos</h4>
                
                {dateGroup.items && dateGroup.items.length > 0 ? (
                  <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
                    {dateGroup.items.map((product, productIndex) => (
                      <li key={productIndex} className="px-4 py-3 flex justify-between items-center">
                        <div>
                          <span className="font-medium">{product.name}</span>
                          <span className="text-gray-500 ml-2">
                            {product.quantity} {product.unit}
                          </span>
                        </div>
                        
                        {deliveryDates.length > 1 && (
                          <select
                            className="border-gray-300 rounded-md text-sm"
                            value={dateIndex}
                            onChange={(e) => moveProductToDate(
                              productIndex, 
                              dateIndex, 
                              parseInt(e.target.value)
                            )}
                          >
                            {deliveryDates.map((_, i) => (
                              <option key={i} value={i}>
                                Data {i + 1}
                              </option>
                            ))}
                          </select>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">
                    Nenhum produto atribuído a esta data. Adicione produtos ou remova esta data.
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DeliveryForm;